import { Preferences } from '@capacitor/preferences';
import { isNativePlatform } from '@/lib/platform';

interface PendingFuelLog {
  company_id: string;
  driver_id: string;
  vehicle_id?: string;
  odometer: number | null;
  liters: number | null;
  fuel_type?: string | null;
  price_unit?: number | null;
  station_id?: string | null;
  location_latitude?: number | null;
  location_longitude?: number | null;
  odometerImageData?: string;
  pumpImageData?: string;
  invoiceImageData?: string;
  ocr_metadata?: any;
  source?: string;
  occurred_at?: string;
  timestamp: number;
  synced: boolean;
}

const PENDING_LOGS_KEY = 'pending_fuel_logs';
const SYNC_STATUS_KEY = 'sync_status';

class OfflineStorageService {
  private async setItem(key: string, value: string): Promise<void> {
    if (isNativePlatform()) {
      await Preferences.set({ key, value });
    } else {
      localStorage.setItem(key, value);
    }
  }

  private async getItem(key: string): Promise<string | null> {
    if (isNativePlatform()) {
      const result = await Preferences.get({ key });
      return result.value;
    } else {
      return localStorage.getItem(key);
    }
  }

  private async removeItem(key: string): Promise<void> {
    if (isNativePlatform()) {
      await Preferences.remove({ key });
    } else {
      localStorage.removeItem(key);
    }
  }

  async savePendingLog(log: PendingFuelLog): Promise<void> {
    const existingLogs = await this.getPendingLogs();
    existingLogs.push(log);
    await this.setItem(PENDING_LOGS_KEY, JSON.stringify(existingLogs));
  }

  async getPendingLogs(): Promise<PendingFuelLog[]> {
    const value = await this.getItem(PENDING_LOGS_KEY);
    return value ? JSON.parse(value) : [];
  }

  async removePendingLog(timestamp: number): Promise<void> {
    const logs = await this.getPendingLogs();
    const filtered = logs.filter(log => log.timestamp !== timestamp);
    await this.setItem(PENDING_LOGS_KEY, JSON.stringify(filtered));
  }

  async clearAllPendingLogs(): Promise<void> {
    await this.removeItem(PENDING_LOGS_KEY);
  }

  async getPendingLogsCount(): Promise<number> {
    const logs = await this.getPendingLogs();
    return logs.length;
  }

  async setSyncStatus(status: 'idle' | 'syncing' | 'error'): Promise<void> {
    await this.setItem(SYNC_STATUS_KEY, status);
  }

  async getSyncStatus(): Promise<string> {
    const value = await this.getItem(SYNC_STATUS_KEY);
    return value || 'idle';
  }

  async saveDriverAuth(driverId: string, token: string): Promise<void> {
    await this.setItem('driver_auth', JSON.stringify({ driverId, token, timestamp: Date.now() }));
  }

  async getDriverAuth(): Promise<{ driverId: string; token: string } | null> {
    const value = await this.getItem('driver_auth');
    if (!value) return null;
    
    const auth = JSON.parse(value);
    const sevenDaysAgo = Date.now() - (7 * 24 * 60 * 60 * 1000);
    if (auth.timestamp < sevenDaysAgo) {
      await this.clearDriverAuth();
      return null;
    }
    
    return { driverId: auth.driverId, token: auth.token };
  }

  async clearDriverAuth(): Promise<void> {
    await this.removeItem('driver_auth');
  }

  async saveOpsManagerAuth(userId: string, token: string, companyId: string): Promise<void> {
    await this.setItem('ops_manager_auth', JSON.stringify({ 
      userId, 
      token, 
      companyId,
      timestamp: Date.now() 
    }));
  }

  async getOpsManagerAuth(): Promise<{ userId: string; token: string; companyId: string } | null> {
    const value = await this.getItem('ops_manager_auth');
    if (!value) return null;
    
    const auth = JSON.parse(value);
    const sevenDaysAgo = Date.now() - (7 * 24 * 60 * 60 * 1000);
    if (auth.timestamp < sevenDaysAgo) {
      await this.clearOpsManagerAuth();
      return null;
    }
    
    return { userId: auth.userId, token: auth.token, companyId: auth.companyId };
  }

  async clearOpsManagerAuth(): Promise<void> {
    await this.removeItem('ops_manager_auth');
  }
}

export const offlineStorageService = new OfflineStorageService();
export type { PendingFuelLog };
